import type * as core from '../../../../../../../core'

// NOTE: projectId is a single-select reference, so its value is
// `{ entityId, recordId }` (or null when no project is selected)
const filter = {
  or: [
    {
      '=': [
        {
          '`': {
            '``': {
              '@$': 'projectId',
            },
          },
        },
        null,
      ],
    },
    {
      '=': [
        {
          '@.': ['!project', { ref: { entity: '!proj' } }],
        },
        {
          '@>': [
            {
              '`': {
                '``': {
                  '@$': 'projectId',
                },
              },
            },
            'recordId',
          ],
        },
      ],
    },
  ],
}

// The nested `content.query`/`content.count` selections above live inside a
// backtick-quoted DMS query block, which is why `filter` above escapes the
// `projectId` variable reference through `` '`' ``/`` '``' ``. The top-level
// `subqueries` entry below is not inside that quoting layer, so its filter
// references the variable directly, matching the same project-membership
// semantics as `filter`.
const topLevelFilter: core.dms.data.expression.fn.Fn<boolean> = {
  or: [
    {
      '=': [{ '@$': 'projectId' }, null],
    },
    {
      '=': [
        {
          '@.': ['!project', { ref: { entity: '!proj' } }],
        },
        {
          '@>': [{ '@$': 'projectId' }, 'recordId'],
        },
      ],
    },
  ],
}

export const query: core.mosaic.Widget.Query = {
  selection: {
    '{}': {
      name: 'Engagement Charter Components',
      type: 'table',
      overflowActions: {
        '[]': [
          {
            '{}': {
              icon: 'atn-icon atn-link',
              name: 'Generate Engagement Charter',
              action: {
                '{}': {
                  payload: {
                    '{}': {
                      agentId: 'engagementCharterGenerator',
                      records: {
                        '??': [
                          {
                            '@': 'engagementCharterComponentRecords',
                          },
                          {
                            '[]': [],
                          },
                        ],
                      },
                    },
                  },
                  operation: 'ui.open-assistant',
                },
              },
            },
          },
        ],
      },
      content: {
        '{}': {
          count: {
            '`': {
              selection: {
                '#': {
                  '@': 'engagementCharterComponents',
                },
              },
              subqueries: {
                engagementCharterComponents: {
                  filter,
                  target:
                    'ent:!engagement_charter_comps:!engagement_charter_comp',
                },
              },
            },
          },
          query: {
            '`': {
              filter,
              sort: {
                by: {
                  '@.': '!recorded_at',
                },
              },
              target: 'ent:!engagement_charter_comps:!engagement_charter_comp',
              selection: {
                '{}': {
                  fields: {
                    '{}': {
                      component: {
                        '{}': {
                          value: {
                            '@.': '!id',
                          },
                        },
                      },
                      project: {
                        '{}': {
                          value: {
                            '??': [
                              {
                                '@': 'projectName',
                              },
                              '—',
                            ],
                          },
                        },
                      },
                      componentType: {
                        '{}': {
                          value: {
                            '@.': '!component_type',
                          },
                        },
                      },
                      actor: {
                        '{}': {
                          value: {
                            '@.': '!actor',
                          },
                        },
                      },
                      recordedAt: {
                        '{}': {
                          value: {
                            '@.': '!recorded_at',
                          },
                        },
                      },
                    },
                  },
                },
              },
              subqueries: {
                projectName: {
                  ref: {
                    attr: 'attr:ent#!engagement_charter_comps#!engagement_charter_comp:!project',
                  },
                  target: 'ent:!projects:!proj',
                  quantity: 1,
                  selection: {
                    '@.': '!name',
                  },
                },
              },
            },
          },
          columns: {
            '[]': [
              {
                '{}': {
                  id: 'component',
                  direct: {
                    '{}': {
                      name: 'Component',
                      type: 'TEXT',
                    },
                  },
                  isSortable: true,
                },
              },
              {
                '{}': {
                  id: 'project',
                  direct: {
                    '{}': {
                      name: 'Project',
                      type: 'TEXT',
                    },
                  },
                  isSortable: true,
                },
              },
              {
                '{}': {
                  id: 'componentType',
                  dmsAttr: {
                    '{}': {
                      attributeId:
                        'attr:ent#!engagement_charter_comps#!engagement_charter_comp:!component_type',
                    },
                  },
                  isSortable: true,
                },
              },
              {
                '{}': {
                  id: 'actor',
                  direct: {
                    '{}': {
                      name: 'Actor',
                      type: 'TEXT',
                    },
                  },
                  isSortable: true,
                },
              },
              {
                '{}': {
                  id: 'recordedAt',
                  direct: {
                    '{}': {
                      name: 'Recorded At',
                      type: 'DATE',
                    },
                  },
                  isSortable: true,
                },
              },
            ],
          },
        },
      },
    },
  },
  variables: {
    projectId: null,
  },
  subqueries: {
    engagementCharterComponentRecords: {
      filter: topLevelFilter,
      target: 'ent:!engagement_charter_comps:!engagement_charter_comp',
      selection: {
        '{}': {
          modelId: 'ent:!engagement_charter_comps:!engagement_charter_comp',
          recordId: {
            '@.': '!id',
          },
        },
      },
    },
  },
}
